import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * MobileGate — decide, em runtime, se renderiza a app mobile ou a versão PC.
 *
 * Em viewport mobile (<= 768px) carrega (lazy) a `MobileApp` isolada em `src/mobile/`.
 * Em qualquer outro caso renderiza `desktop` (a árvore atual do PC), que fica intacta.
 *
 * Deteção síncrona no inicializador do estado para não haver "flash" de desktop
 * no primeiro paint em telemóvel. Não depende do hook partilhado para manter
 * o comportamento do PC byte-a-byte.
 */

const MOBILE_QUERY = '(max-width: 768px)';

const MobileApp = lazy(() => import('./MobileApp'));

/** Ecrãs que valem a pena mostrar na versão da app mesmo num PC grande. */
const SEMPRE_NA_APP = [/^\/join\//];

function detectMobile(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  // Permite forçar PC para testes: ?desktop=1
  const params = new URLSearchParams(window.location.search);
  if (params.get('desktop') === '1') return false;
  // O convite é a primeira coisa que alguém vê do PadelMaster, e a versão da
  // app é a que está trabalhada — não faz sentido servir outra a quem abre o
  // link no computador.
  if (SEMPRE_NA_APP.some((r) => r.test(window.location.pathname))) return true;
  return window.matchMedia(MOBILE_QUERY).matches;
}

function MobileBootSplash() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        background: '#ffffff',
      }}
      aria-busy="true"
      aria-label="A carregar"
    >
      <img src="/icons/pwa-192.png" alt="" width={72} height={72} style={{ opacity: 0.9 }} />
    </div>
  );
}

interface MobileGateProps {
  desktop: React.ReactNode;
}

const MobileGate: React.FC<MobileGateProps> = ({ desktop }) => {
  const { pathname } = useLocation();
  const [isMobile, setIsMobile] = useState<boolean>(detectMobile);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(detectMobile());
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  /* Rever a cada navegação: o convite força a versão da app, e sem isto essa
     decisão colava-se à sessão inteira — saía-se do convite num PC e continuava
     tudo na versão de telemóvel até recarregar a página. */
  useEffect(() => {
    setIsMobile(detectMobile());
  }, [pathname]);

  if (isMobile) {
    return (
      <Suspense fallback={<MobileBootSplash />}>
        <MobileApp />
      </Suspense>
    );
  }

  return <>{desktop}</>;
};

export default MobileGate;
